// Generic "is this background too dark for light-mode (dark) text" check,
// used by the mobile app theme preview (Customize > Themes) to decide when
// to swap light-mode text/icon colors for dark-mode equivalents. Distinct
// from concierge-luminance.ts, which resolves a specific brand ink for the
// AI Concierge surface — this module only answers the boolean "is the
// configured background dark enough that default (near-black) text/icons
// would fail WCAG AA contrast against it?"
//
// Call sites (preview styled-components) read `theme.isBackgroundDark` and
// pick their own light/dark hex pairs — those light-mode inks vary by
// component (#242526, #2c3236, #454e50, …), so there is no shared color
// map here; this module only owns the boolean threshold.
import { isValidThemeHex, relativeLuminance, contrastRatio } from './concierge-luminance';

// WCAG 2.1 AA minimum contrast ratio for normal-size text (4.5:1). This is
// the threshold called out in the ticket/spec: below it, default dark text
// on the configured background can't meet AA, so we flip to dark-mode
// (light) text instead of leaving light-mode text on a too-dark background.
export const AA_NORMAL_TEXT_CONTRAST_RATIO = 4.5;

// Default light-mode "primary" text color used as the reference ink when
// deciding whether the background is dark enough to require inversion.
// Display colors in the preview may differ; this is only the WCAG probe.
const LIGHT_MODE_PRIMARY_TEXT_HEX = '#121516';

/**
 * Returns true when `backgroundHex` is dark enough that the default
 * light-mode (near-black) text color would fail WCAG AA contrast
 * (`minContrastRatio`, default 4.5:1) against it — i.e. the background is
 * "too dark" and dark-mode (light) text/icons should be used instead.
 *
 * Falls back to `false` (preserve light mode) for missing/invalid input so
 * unset or malformed background colors never accidentally trigger dark
 * mode — this is the "no regression for default/light backgrounds"
 * acceptance criterion.
 */
export function isBackgroundTooDark(
  backgroundHex: string | null | undefined,
  minContrastRatio: number = AA_NORMAL_TEXT_CONTRAST_RATIO
): boolean {
  if (!isValidThemeHex(backgroundHex)) {
    return false;
  }

  return contrastRatio(LIGHT_MODE_PRIMARY_TEXT_HEX, backgroundHex) < minContrastRatio;
}

// Re-exported for call sites that want the raw luminance (e.g. to layer on
// additional checks) without importing concierge-luminance.ts directly.
export { relativeLuminance };
